import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send, X, RefreshCw, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import * as api from '../services/api';

/**
 * ArticleChat - Chat contextual para artículos
 * Solo responde preguntas relacionadas con el artículo actual
 * Mantiene la línea editorial de derecha capitalista libertaria
 */
const ArticleChat = ({ dossierId, articleTitle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `¡Hola! Soy el asistente de Influencia & Poder. Estoy aquí para ayudarte a entender mejor este artículo: "${articleTitle}". ¿Qué te gustaría saber?`
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.chatWithArticle(dossierId, userMessage);
      
      if (response.success) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: response.response 
        }]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Lo siento, hubo un error. Por favor intenta de nuevo.' 
        }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Error de conexión. Por favor intenta de nuevo.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Suggested questions
  const suggestions = [
    '¿Por qué es importante esta noticia?',
    '¿Quién se beneficia de esto?',
    '¿Cuál es el impacto económico?',
    '¿Qué significa para los inversores?'
  ];

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-black text-white p-4 rounded-full shadow-lg hover:bg-gray-800 transition-all z-50 flex items-center gap-2"
        data-testid="article-chat-toggle"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="hidden md:inline text-sm font-medium">Pregunta sobre este artículo</span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-[380px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 flex flex-col max-h-[500px]" data-testid="article-chat-panel">
      {/* Header */}
      <div className="bg-black text-white px-4 py-3 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          <div>
            <h3 className="font-semibold text-sm">Asistente Editorial</h3>
            <p className="text-xs text-gray-400 truncate max-w-[200px]">{articleTitle}</p>
          </div>
        </div>
        <button 
          onClick={() => setIsOpen(false)}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[200px] max-h-[300px]">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                msg.role === 'user'
                  ? 'bg-black text-white rounded-br-md'
                  : 'bg-gray-100 text-gray-800 rounded-bl-md'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
              <RefreshCw className="w-4 h-4 animate-spin text-gray-500" />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions - only show if no user messages yet */}
      {messages.length <= 1 && (
        <div className="px-4 pb-2">
          <p className="text-xs text-gray-500 mb-2">Preguntas sugeridas:</p>
          <div className="flex flex-wrap gap-1">
            {suggestions.map((q, i) => (
              <button
                key={i}
                onClick={() => setInput(q)}
                className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded-full transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-3 border-t">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Pregunta sobre este artículo..."
            className="flex-1 text-sm"
            disabled={loading}
            data-testid="article-chat-input"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            className="bg-black hover:bg-gray-800 px-3"
            data-testid="article-chat-send"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-[10px] text-gray-400 mt-2 text-center">
          Solo respondo preguntas sobre este artículo
        </p>
      </div>
    </div>
  );
};

export default ArticleChat;
